// src/ui/tutorial.ts
// First-run, icon-first 2-step visual tutorial (spec §5.5): drag a block → it slides into its
// matching-color door. No text walls — the whole lesson is an animated mini-board demo (pre-reader
// friendly). Dismissable by tap, the big check button, or Esc, and fully skippable. The once-only
// gate lives in storage (`tutorialSeen`); this class only owns show()/dispose() of the overlay.

function requireEl<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (el === null) {
    throw new Error(`Tutorial: missing required element #${id}`);
  }
  return el as T;
}

export class Tutorial {
  private readonly overlay: HTMLElement;
  private readonly dismissBtn: HTMLButtonElement;
  private readonly onDismiss: () => void;
  private dismissed = false;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onOverlayClick: () => void;

  constructor(onDismiss: () => void) {
    this.onDismiss = onDismiss;
    this.overlay = requireEl('tutorial-overlay');
    this.dismissBtn = requireEl<HTMLButtonElement>('tutorial-dismiss');

    this.onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.dismiss();
      }
    };
    // Tapping anywhere on the overlay (backdrop or card) dismisses.
    this.onOverlayClick = () => this.dismiss();

    this.dismissBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.dismiss();
    });
    this.overlay.addEventListener('click', this.onOverlayClick);
  }

  /** Reveal the tutorial. `reducedMotion` freezes the demo to a static, arrow-cued frame (§5.4). */
  show(reducedMotion: boolean): void {
    this.overlay.classList.toggle('reduced', reducedMotion);
    this.overlay.hidden = false;
    document.addEventListener('keydown', this.onKeyDown, true);
    this.dismissBtn.focus({ preventScroll: true });
  }

  /** Tear down listeners and hide. Safe to call once dismissed. */
  dispose(): void {
    document.removeEventListener('keydown', this.onKeyDown, true);
    this.overlay.removeEventListener('click', this.onOverlayClick);
    this.overlay.hidden = true;
  }

  private dismiss(): void {
    if (this.dismissed) {
      return;
    }
    this.dismissed = true;
    document.removeEventListener('keydown', this.onKeyDown, true);
    this.overlay.removeEventListener('click', this.onOverlayClick);
    this.overlay.hidden = true;
    this.onDismiss();
  }
}
